import Browser from 'bowser';
import { forEach, get as _get, parseInt } from 'lodash-es';
import type { ComponentType, FC, ReactElement } from 'react';
import { createContext, memo } from 'react';
import { connect } from 'react-redux';

import type { AuthState, AuthUser } from 'common/auth/types';

export interface Logger {
    initialize(): void;

    log(...e: unknown[]): void;
    info(...e: unknown[]): void;
    warn(...e: unknown[]): void;
    error(...e: unknown[]): void;

    flush(timeoutMs?: number): Promise<void>;
}

export interface UserMeta {
    userPk?: string | null;
    sessionId?: string | null;
    browser?: Browser.Parser.Details;
    engine?: Browser.Parser.Details;
    os?: Browser.Parser.OSDetails;
    platform?: Browser.Parser.PlatformDetails;
    location?: string;
    hostname?: string;
}

export class CILogger {
    private static __instance: CILogger = new CILogger();

    public static getInstance(): CILogger {
        return CILogger.__instance;
    }

    private providers: Logger[] = [];
    // An unset REACT_APP_LOGLEVEL parses to NaN, which disables all levels
    private logLevel: number = parseInt(process.env.REACT_APP_LOGLEVEL as string);
    private currentSession: UserMeta = {};

    public setAuth(auth: AuthUser | null | undefined): void {
        this.currentSession = {
            userPk: (auth && auth.pk) || null,
            sessionId: (auth && auth.sessionId) || null,
            hostname: _get(window, 'location.hostname'),
            location: _get(window, 'location.href'),
            // parse is not exported from bowser as a separate function. It is exported as Browser.parse
            // eslint-disable-next-line import/no-named-as-default-member
            ...Browser.parse(window.navigator.userAgent),
        };
    }

    // Append/Update log data
    private appendDefaultLogData(): void {
        this.currentSession.location = _get(window, 'location.href');
    }

    public initialize(): void {
        forEach(this.providers, (provider): void => provider.initialize());
    }

    public registerProvider(provider: Logger): void {
        this.providers.push(provider);
    }

    public log(...e: unknown[]): void {
        if (this.logLevel === 0) {
            this.appendDefaultLogData();
            forEach(this.providers, (provider): void => provider.log(e, this.currentSession));
        }
    }

    public info(...e: unknown[]): void {
        if (this.logLevel <= 1) {
            this.appendDefaultLogData();
            forEach(this.providers, (provider): void => provider.info(e, this.currentSession));
        }
    }

    public warn(...e: unknown[]): void {
        if (this.logLevel <= 2) {
            this.appendDefaultLogData();
            forEach(this.providers, (provider): void => provider.warn(e, this.currentSession));
        }
    }

    public error(...e: unknown[]): void {
        if (this.logLevel <= 3) {
            this.appendDefaultLogData();
            forEach(this.providers, (provider): void => provider.error(e, this.currentSession));
        }
    }

    public async flush(timeoutMs: number = 5000): Promise<void> {
        await Promise.allSettled(this.providers.map(async (provider) => provider.flush(timeoutMs)));
    }
}

const LoggingContext = createContext(CILogger.prototype);

export default function LoggerProvider(WrappedComponent: ComponentType): ComponentType {
    interface Props {
        auth: AuthState;
    }

    const _LoggerProvider: FC<Props> = (props): ReactElement => {
        const logger = CILogger.getInstance();
        logger.setAuth(props.auth.activeUser);

        return (
            <LoggingContext.Provider value={logger}>
                <WrappedComponent />
            </LoggingContext.Provider>
        );
    };

    const LoggerProvider = memo(_LoggerProvider, (prevProps, nextProps): boolean => nextProps.auth === prevProps.auth);

    const mapStateToProps = ({ auth }: { auth: AuthState }): Props => ({ auth });

    return connect(mapStateToProps)(LoggerProvider);
}
/* eslint-disable react-refresh/only-export-components */
export function withLogger<P extends object>(Component: ComponentType<P & { logger: CILogger }>): ComponentType<P> {
    return function WrapperComponent(props: P): ReactElement {
        return (
            <LoggingContext.Consumer>
                {(logger): ReactElement => <Component {...props} logger={logger} />}
            </LoggingContext.Consumer>
        );
    };
}
